import { useEffect, useState, useRef } from "react";
import api, { formatEUR, formatDateFR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, CheckCircle2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Import() {
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [rows, setRows] = useState([]);
  const [defaultExpense, setDefaultExpense] = useState("");
  const [defaultIncome, setDefaultIncome] = useState("");
  const fileRef = useRef();

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([api.get("/accounts"), api.get("/categories")]);
      setAccounts(a.data);
      setCategories(c.data);
      if (a.data[0]) setAccountId(a.data[0].id);
      const exp = c.data.find((cc) => cc.type === "expense");
      const inc = c.data.find((cc) => cc.type === "income");
      if (exp) setDefaultExpense(exp.id);
      if (inc) setDefaultIncome(inc.id);
    })();
  }, []);

  const upload = async (file) => {
    setUploading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/preview", fd);
      setPreview(data);
      setRows(data.preview.map((r) => ({ ...r, keep: true })));
      toast.success(`${data.detected} transaction(s) détectée(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'analyse");
    } finally {
      setUploading(false);
    }
  };

  const confirm = async () => {
    if (!accountId) return toast.error("Choisissez un compte");
    const kept = rows.filter((r) => r.keep);
    if (!kept.length) return toast.error("Aucune ligne sélectionnée");
    setConfirming(true);
    try {
      const payload = {
        account_id: accountId,
        rows: kept.map((r) => ({
          date: r.date,
          description: r.description || "Import",
          amount: r.amount,
          type: r.type,
          category_id: r.category_id || (r.type === "income" ? defaultIncome : defaultExpense),
        })),
      };
      const { data } = await api.post("/import/confirm", payload);
      toast.success(`${data.inserted} transaction(s) importée(s)`);
      setPreview(null); setRows([]);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally {
      setConfirming(false);
    }
  };

  const cats = (type) => categories.filter((c) => c.type === type);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Import bancaire</h1>
        <p className="text-stone-500 mt-1">Importez un relevé CSV pour créer vos transactions en un clic.</p>
      </div>

      {!preview && (
        <Card className="p-8 rounded-2xl border-2 border-dashed border-stone-300 bg-white text-center">
          <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-4">
            <FileSpreadsheet className="w-8 h-8 text-stone-600" />
          </div>
          <h3 className="font-display text-xl font-semibold mb-2">Glissez votre fichier CSV</h3>
          <p className="text-sm text-stone-500 mb-5">Formats supportés: relevés bancaires CSV français (BNP, Société Générale, Crédit Agricole, N26, Boursorama...)</p>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                 onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                 data-testid="import-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="rounded-xl bg-stone-900 hover:bg-stone-800"
                  data-testid="import-upload-button">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyse...</> : <><Upload className="w-4 h-4 mr-2" /> Choisir un fichier</>}
          </Button>
        </Card>
      )}

      {preview && (
        <>
          <Card className="p-6 rounded-2xl border-stone-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-stone-500 font-medium">Compte de destination</label>
                <Select value={accountId} onValueChange={setAccountId}>
                  <SelectTrigger className="mt-1 rounded-xl h-10" data-testid="import-account-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-stone-500 font-medium">Catégorie dépenses par défaut</label>
                <Select value={defaultExpense} onValueChange={setDefaultExpense}>
                  <SelectTrigger className="mt-1 rounded-xl h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats("expense").map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-stone-500 font-medium">Catégorie revenus par défaut</label>
                <Select value={defaultIncome} onValueChange={setDefaultIncome}>
                  <SelectTrigger className="mt-1 rounded-xl h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats("income").map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 text-xs text-stone-500">
              {preview.total_rows} ligne(s) lue(s) · {preview.detected} transaction(s) valide(s)
            </div>
          </Card>

          <Card className="rounded-2xl border-stone-200 overflow-hidden">
            <div className="max-h-[500px] overflow-y-auto divide-y divide-stone-100">
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-stone-50" data-testid={`import-row-${i}`}>
                  <input type="checkbox" checked={r.keep}
                         onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, keep: e.target.checked } : x))}
                         className="w-4 h-4 rounded" />
                  <div className="w-20 text-xs text-stone-500 font-mono-num">{formatDateFR(r.date)}</div>
                  <div className="flex-1 text-sm truncate">{r.description}</div>
                  <Select value={r.category_id || (r.type === "income" ? defaultIncome : defaultExpense)}
                          onValueChange={(v) => setRows(rows.map((x, j) => j === i ? { ...x, category_id: v } : x))}>
                    <SelectTrigger className="w-40 h-8 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{cats(r.type).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className={`text-sm font-semibold font-mono-num w-24 text-right ${r.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                    {r.type === "income" ? "+" : "−"}{formatEUR(r.amount)}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => { setPreview(null); setRows([]); if (fileRef.current) fileRef.current.value = ""; }}
                    className="rounded-xl" data-testid="import-cancel-button">
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={confirm} disabled={confirming}
                    className="rounded-xl bg-stone-900 hover:bg-stone-800"
                    data-testid="import-confirm-button">
              {confirming ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Import...</>
                          : <><CheckCircle2 className="w-4 h-4 mr-2" /> Importer {rows.filter((r) => r.keep).length} transactions</>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
